/**
 * @file footer-docs.ts
 * @description Свои строки блока «Ссылки» подвала — раздел `theme.footerDocs`
 * документа оформления (phase19.16, партия «Юридические документы», SB142;
 * проект данных docs/phases/phase19.16-materials/db-design-legal-docs.md §6.1).
 *
 * Строка — документ или группа документов (`kind: "document"`, по id) либо
 * «Все документы» (`/legal`); у строки переключатель вкл/выкл и якорь
 * `after` — после какой ссылки платформы она стоит (null — первой).
 *
 * Почему отдельный раздел темы, а не `"doc:<id>"` в `footer.legal.linksOrder`:
 * `readSiteTheme` заменяет умолчанием раздел, не прошедший строгую схему, —
 * старый код при откате увидел бы чужое значение в `linksOrder` и сбросил бы
 * весь подвал (цвета, раскладку), в том числе на опубликованном сайте.
 * Незнакомый раздел старый код молча отбрасывает (страж отката SB120 этапа 4):
 * при откате пропадут только свои строки. Порядок ссылок платформы остаётся
 * единственным — `footer.legal.linksOrder`; строка привязана к соседней
 * ссылке платформы, поэтому второго источника порядка нет. Выключенные и
 * скрытые ссылки платформы тоже служат якорем — порядок не «прыгает».
 *
 * Кабинет показывает общий список вперемешку (`mergeFooterRows`), при
 * перетаскивании работает с ключами (`footerLinkKey`: платформенный ключ,
 * `doc:<id>`, `all-documents`) и раскладывает список обратно
 * (`footerEntriesFromKeys` → `splitFooterRows`). Сервер собирает ссылки
 * сайта `interleaveFooterDocItems` (site-config-builder, при сборке ответа).
 *
 * Решение оркестратора 2026-10-19 (вопрос 8): свои строки — содержание сайта,
 * дизайн страницы их не перекрывает (design.ts). Модуль не импортирует
 * schema.ts (schema.ts импортирует его) — циклов нет.
 */

import { z } from "zod";

import { FOOTER_PLATFORM_LINKS, orderFooterPlatformLinks, type FooterPlatformLink } from "./footer-legal.js";
import { deepFreeze } from "./deep-freeze.js";
import { SITE_THEME_LIMITS } from "./theme-limits.js";

/** Жёсткий предел строк в разделе (схема). Мягкий — `FOOTER_DOCS_SOFT_LIMIT` (legal). */
export const FOOTER_DOCS_MAX: number = SITE_THEME_LIMITS.footerDocs.max;

/** id документа в строке: cuid и подобные — без `:`, `/` и пробелов (ключ `doc:<id>` однозначен). */
export const FOOTER_DOC_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

/** Якорь строки: после какой ссылки платформы; null — в начале. */
const anchor = z.enum(FOOTER_PLATFORM_LINKS).nullable();

/** Строка: документ или группа по id. */
export const footerDocDocumentItemSchema = z.strictObject({
  kind: z.literal("document"),
  documentId: z.string().regex(FOOTER_DOC_ID_PATTERN),
  enabled: z.boolean(),
  after: anchor,
});

/** Строка «Все документы» → `/legal` (SB141, SB142). */
export const footerDocAllItemSchema = z.strictObject({
  kind: z.literal("allDocuments"),
  enabled: z.boolean(),
  after: anchor,
});

export const footerDocItemSchema = z.discriminatedUnion("kind", [footerDocDocumentItemSchema, footerDocAllItemSchema]);
export type FooterDocItem = z.infer<typeof footerDocItemSchema>;
export type FooterDocDocumentItem = z.infer<typeof footerDocDocumentItemSchema>;

/** Ключ строки «Все документы» в общем списке (не похож ни на ключ платформы, ни на `doc:`). */
export const FOOTER_ALL_DOCUMENTS_KEY = "all-documents";

/** Префикс ключа строки-документа. */
export const FOOTER_DOC_KEY_PREFIX = "doc:";

/** Ключ строки: `doc:<id>` или `all-documents`. */
export function footerDocItemKey(item: FooterDocItem): string {
  return item.kind === "document" ? `${FOOTER_DOC_KEY_PREFIX}${item.documentId}` : FOOTER_ALL_DOCUMENTS_KEY;
}

/** Документ не повторяется, «Все документы» — не больше одной строки. */
function uniqueItems(items: readonly FooterDocItem[]): boolean {
  return new Set(items.map(footerDocItemKey)).size === items.length;
}

export const siteFooterDocsSchema = z.strictObject({
  items: z
    .array(footerDocItemSchema)
    .max(SITE_THEME_LIMITS.footerDocs.max)
    .refine(uniqueItems, "Документ в «Ссылках» указан дважды"),
});
export type SiteFooterDocs = z.infer<typeof siteFooterDocsSchema>;

/** Умолчание — своих строк нет (подвал как до партии). */
export const DEFAULT_SITE_FOOTER_DOCS: SiteFooterDocs = deepFreeze({ items: [] });

// ── Терпимое чтение (до проверки схемой, schema.ts) ─────────────────────────

/** Что снято при чтении: путь и причина словами (форма `SiteThemeIssue`). */
export interface FooterDocsIssue {
  path: string;
  message: string;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Починка сохранённого раздела по строкам: битая строка (неверный id,
 * незнакомый якорь или вид, лишний ключ), повтор и строки сверх предела
 * снимаются по одной — остальные сохраняются. Лишние ключи раздела (будущая
 * версия после отката) снимаются. Не объект или нет списка — как есть:
 * решит схема (раздел целиком умолчанием).
 */
export function repairFooterDocs(value: unknown): { value: unknown; issues: FooterDocsIssue[] } {
  if (!isPlainObject(value) || !Array.isArray(value["items"])) return { value, issues: [] };
  const issues: FooterDocsIssue[] = [];
  for (const key of Object.keys(value)) {
    if (key !== "items") issues.push({ path: `footerDocs.${key}`, message: "Лишнее поле снято" });
  }
  const kept: FooterDocItem[] = [];
  const seen = new Set<string>();
  (value["items"] as unknown[]).forEach((raw, index) => {
    const path = `footerDocs.items.${index}`;
    const parsed = footerDocItemSchema.safeParse(raw);
    if (!parsed.success) {
      issues.push({ path, message: "Строка «Ссылок» подвала не прошла проверку — снята" });
      return;
    }
    const key = footerDocItemKey(parsed.data);
    if (seen.has(key)) {
      issues.push({ path, message: "Строка «Ссылок» подвала повторяется — снята" });
      return;
    }
    if (kept.length >= FOOTER_DOCS_MAX) {
      issues.push({ path, message: `Строк «Ссылок» больше ${FOOTER_DOCS_MAX} — лишняя снята` });
      return;
    }
    seen.add(key);
    kept.push(parsed.data);
  });
  return { value: { items: kept }, issues };
}

// ── Общий список «вперемешку» ───────────────────────────────────────────────

/** Элемент общего списка: ссылка платформы или своя строка. */
export type FooterLinkEntry = { platform: FooterPlatformLink } | { item: FooterDocItem };

/** Ключ элемента общего списка (id для перетаскивания): `offer`, `doc:<id>`, `all-documents`. */
export function footerLinkKey(entry: FooterLinkEntry): string {
  return "platform" in entry ? entry.platform : footerDocItemKey(entry.item);
}

/**
 * Общий список для кабинета и сборщика: строки с якорем null — первыми,
 * затем каждая ссылка платформы (в порядке `orderFooterPlatformLinks`, все
 * пять — и выключенные, они тоже якорь) и её строки в порядке массива.
 */
export function mergeFooterRows(
  linksOrder: readonly FooterPlatformLink[],
  items: readonly FooterDocItem[],
): FooterLinkEntry[] {
  const out: FooterLinkEntry[] = items.filter((item) => item.after === null).map((item) => ({ item }));
  for (const platform of orderFooterPlatformLinks(linksOrder)) {
    out.push({ platform });
    for (const item of items) if (item.after === platform) out.push({ item });
  }
  return out;
}

/**
 * Обратно из общего списка: порядок ссылок платформы (полный, недостающие —
 * следом, как `orderFooterPlatformLinks`) и строки с пересчитанным якорем —
 * последней ссылкой платформы перед строкой (null — строка в начале).
 * Повторы ключей снимаются (первый остаётся).
 */
export function splitFooterRows(entries: readonly FooterLinkEntry[]): {
  linksOrder: FooterPlatformLink[];
  items: FooterDocItem[];
} {
  const order: FooterPlatformLink[] = [];
  const items: FooterDocItem[] = [];
  const seen = new Set<string>();
  let after: FooterPlatformLink | null = null;
  for (const entry of entries) {
    const key = footerLinkKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    if ("platform" in entry) {
      order.push(entry.platform);
      after = entry.platform;
    } else {
      items.push({ ...entry.item, after });
    }
  }
  return { linksOrder: orderFooterPlatformLinks(order), items };
}

/**
 * Общий список из ключей (порядок после перетаскивания в кабинете): ключ
 * платформы — ссылка платформы; `doc:<id>` и `all-documents` — строка из
 * `items` (с её «вкл/выкл») или новая включённая строка; ключ с неверным id,
 * незнакомый ключ и повтор отбрасываются. Якоря пересчитает `splitFooterRows`.
 */
export function footerEntriesFromKeys(keys: readonly string[], items: readonly FooterDocItem[]): FooterLinkEntry[] {
  const byKey = new Map(items.map((item) => [footerDocItemKey(item), item] as const));
  const platform: ReadonlySet<string> = new Set(FOOTER_PLATFORM_LINKS);
  const seen = new Set<string>();
  const out: FooterLinkEntry[] = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    let entry: FooterLinkEntry | null = null;
    if (platform.has(key)) {
      entry = { platform: key as FooterPlatformLink };
    } else if (key === FOOTER_ALL_DOCUMENTS_KEY) {
      entry = { item: byKey.get(key) ?? { kind: "allDocuments", enabled: true, after: null } };
    } else if (key.startsWith(FOOTER_DOC_KEY_PREFIX)) {
      const documentId = key.slice(FOOTER_DOC_KEY_PREFIX.length);
      if (FOOTER_DOC_ID_PATTERN.test(documentId)) {
        entry = { item: byKey.get(key) ?? { kind: "document", documentId, enabled: true, after: null } };
      }
    }
    if (entry === null) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}

/**
 * Ссылки нижней полосы сайта вперемешку со своими строками (сборщик
 * site-config, §6.1). `platformOrder` — полный порядок ссылок платформы
 * (`orderFooterPlatformLinks(linksOrder)`, все пять: скрытые тоже якорь);
 * `platformLink` — видимая ссылка или null (нет документа, дубль меню SB115,
 * выключена SB118); `itemLink` — ссылка строки или null (документ не виден
 * или удалён — SB16, адрес совпал с видимым пунктом меню или уже выданной
 * ссылкой). Выключенные строки до `itemLink` не доходят.
 */
export function interleaveFooterDocItems<P, D>(
  platformOrder: readonly FooterPlatformLink[],
  items: readonly FooterDocItem[],
  platformLink: (key: FooterPlatformLink) => P | null,
  itemLink: (item: FooterDocItem) => D | null,
): Array<P | D> {
  const out: Array<P | D> = [];
  const placed = new Set<FooterDocItem>();
  const emitItems = (anchorKey: FooterPlatformLink | null): void => {
    for (const item of items) {
      if (item.after !== anchorKey || placed.has(item)) continue;
      placed.add(item);
      if (!item.enabled) continue;
      const link = itemLink(item);
      if (link !== null) out.push(link);
    }
  };
  emitItems(null);
  for (const key of orderFooterPlatformLinks(platformOrder)) {
    const link = platformLink(key);
    if (link !== null) out.push(link);
    emitItems(key);
  }
  return out;
}

// ── Кабинет: состояние строки и предупреждения ──────────────────────────────

/** Состояние строки в кабинете: выключена, видна, документ не опубликован, документ удалён. */
export type FooterDocItemStatus = "off" | "ok" | "hidden" | "deleted";

export const FOOTER_DOC_ITEM_STATUS_LABELS: Readonly<Record<FooterDocItemStatus, string>> = deepFreeze({
  off: "выключена",
  ok: "",
  hidden: "не опубликован",
  deleted: "документ удалён — уберите строку",
});

/**
 * Состояние строки. `documents` — документы и группы магазина по id с
 * признаком «виден на сайте» (`isLegalDocumentVisible`); нет в карте —
 * удалён. «Все документы» видна, если виден хоть один документ.
 */
export function footerDocItemStatus(
  item: FooterDocItem,
  documents: ReadonlyMap<string, { visible: boolean }>,
): FooterDocItemStatus {
  if (!item.enabled) return "off";
  if (item.kind === "allDocuments") {
    return [...documents.values()].some((d) => d.visible) ? "ok" : "hidden";
  }
  const doc = documents.get(item.documentId);
  if (!doc) return "deleted";
  return doc.visible ? "ok" : "hidden";
}

export type FooterDocsWarningCode = "footer_docs_many";

export interface FooterDocsWarning {
  code: FooterDocsWarningCode;
  /** Сколько включённых своих строк. */
  count: number;
}

export const FOOTER_DOCS_WARNING_MESSAGES: Readonly<Record<FooterDocsWarningCode, string>> = deepFreeze({
  footer_docs_many: `Больше ${SITE_THEME_LIMITS.footerDocs.warnAbove} своих ссылок — строка внизу становится длинной. Остальное лучше вынести в меню подвала.`,
});

/** SB142: мягкое предупреждение после `warnAbove` включённых своих строк. */
export function footerDocsWarnings(items: readonly FooterDocItem[]): FooterDocsWarning[] {
  const count = items.filter((item) => item.enabled).length;
  return count > SITE_THEME_LIMITS.footerDocs.warnAbove ? [{ code: "footer_docs_many", count }] : [];
}

/** Документы, на которые ссылаются строки («Где используется» в кабинете и api). */
export function footerDocsDocumentIds(docs: Pick<SiteFooterDocs, "items"> | undefined): string[] {
  return (docs?.items ?? []).flatMap((item) => (item.kind === "document" ? [item.documentId] : []));
}

// ── «Что изменено» ──────────────────────────────────────────────────────────

/** Подпись строки «Что изменено». */
export const FOOTER_DOCS_DIFF_LABEL = "Нижняя полоса: свои ссылки";

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

/** Сводка раздела словами: «нет», «2 строки», «3 строки, 1 выключена». */
export function footerDocsSummary(docs: Pick<SiteFooterDocs, "items"> | undefined): string {
  const items = docs?.items ?? [];
  if (items.length === 0) return "нет";
  const off = items.filter((item) => !item.enabled).length;
  const total = `${items.length} ${plural(items.length, "строка", "строки", "строк")}`;
  return off === 0 ? total : `${total}, ${off} ${plural(off, "выключена", "выключены", "выключено")}`;
}
